var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import WidgetRenderer from '@nykaa/deals-widgets';
import { EAGER_LOAD_IMAGES, EAGER_LOAD_INVENTORIES } from '@nykaa/deals-utils/images/constants';
import { DEFAULT_ROOT_MARGIN, IMPRESSION_TYPE } from '@nykaa/deals-utils/impressions/constants';
import apiService from './helper';
import Utils from './utils';
const PersonalizedWidgetRenderer = ({ widgetList, isServer, impressionEvent, asyncImpressionCallback, syncImpressionCallback, eagerLoadImages = EAGER_LOAD_IMAGES, eagerLoadInventories = EAGER_LOAD_INVENTORIES, theme, ApiHelper, dealsEndpoint, store, variants = {}, isDesktop, pageData = 'default', fontCDNPath = '', shouldLoadFonts = false, debug = false, isPersonalizationSupported, cta, rootMargin = DEFAULT_ROOT_MARGIN, remoteConfigs = {}, contextPayload = {}, apiParams, isPaginationEnabled, currentBatchLoadingCb, }) => {
    var _a;
    const [data, setData] = useState({ widgetList: widgetList ? [...widgetList] : [] });
    const paginationObj = useRef({
        batchesMap: {
            currentBatchToBeExecuted: 'batch1',
            batches: {},
        },
        pageFetchCompleted: false,
        totalInventoryCount: ((_a = apiParams === null || apiParams === void 0 ? void 0 : apiParams.paginationParams) === null || _a === void 0 ? void 0 : _a.initialInventoryCount) || 0,
    });
    const getPersonalizedWidgetList = (widgets) => __awaiter(void 0, void 0, void 0, function* () {
        if (!isServer && !!isPersonalizationSupported && widgets && widgets.length > 0) {
            const personalizedWidgetList = yield apiService.getPersonalizedData(ApiHelper, widgets, dealsEndpoint, variants, store, !!isDesktop, debug, pageData, contextPayload);
            return personalizedWidgetList;
        }
        return widgets;
    });
    const getPersonalizedWidgetListOnInitiation = () => {
        const getPersonalizedData = () => __awaiter(void 0, void 0, void 0, function* () {
            const widgets = yield getPersonalizedWidgetList(data.widgetList);
            setData({ widgetList: widgets });
        });
        getPersonalizedData();
    };
    const updateDataOnWidgetListChange = () => {
        if (widgetList && widgetList.length > 0 && data.widgetList.length === 0) {
            setData({ widgetList: widgetList ? [...widgetList] : [] });
        }
    };
    /**
     * Update state widget list on pagination data received with personalization
     * @param pageFetchCompleted: boolean
     * @param widgetData: []
     * @param callbackFn Callback to update batches only after personalization call is success/fail.
     */
    const updatePageDataCallback = ({ pageFetchCompleted, widgetData, callbackFn, }) => {
        const getPersonalizedData = () => __awaiter(void 0, void 0, void 0, function* () {
            const widgets = yield getPersonalizedWidgetList(widgetData);
            setData((arg) => {
                var _a;
                const allWidgets = [...arg.widgetList, ...widgets];
                const allInventoryCount = ((_a = apiParams === null || apiParams === void 0 ? void 0 : apiParams.paginationParams) === null || _a === void 0 ? void 0 : _a.initialInventoryCount) || 0;
                const belowTheFoldInventoryCount = widgetList.length || 0;
                paginationObj.current = Object.assign(Object.assign({}, paginationObj.current), { totalInventoryCount: allWidgets.length
                        + (allInventoryCount - belowTheFoldInventoryCount), pageFetchCompleted });
                return { widgetList: allWidgets };
            });
            if (callbackFn) {
                callbackFn();
            }
        });
        getPersonalizedData();
    };
    /**
   * Update batches state and updates loading state of current batch
   * @param batchesMap: BatchesMap
   */
    const updateBatchStateCallback = (batchesMap) => {
        paginationObj.current = Object.assign(Object.assign({}, paginationObj.current), { batchesMap });
        const { batchesMap: obj } = paginationObj.current;
        const { batches, currentBatchToBeExecuted } = obj;
        const isLoading = batches
            && batches[currentBatchToBeExecuted]
            && batches[currentBatchToBeExecuted].isBatchLoading;
        currentBatchLoadingCb(isLoading);
    };
    /**
     * Pagination Utils generator on initiation like batchesMap, paginationObject
     */
    const paginationUtilsGenerator = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!isPaginationEnabled
            || (isPaginationEnabled
                && apiParams
                && apiParams.paginationParams
                && apiParams.paginationParams.remainingInventoryIds
                && !apiParams.paginationParams.remainingInventoryIds.length)) {
            return;
        }
        const { paginationParams } = apiParams;
        const { initialInventoryCount, remainingInventoryIds, bufferSize, pageLimit, noImpressionImmediatePaginationCall, } = paginationParams;
        const { batchesMap } = Utils.processRemainingInventories(initialInventoryCount, remainingInventoryIds, bufferSize, pageLimit);
        if (noImpressionImmediatePaginationCall) {
            const apiUtils = {
                ApiHelper,
                dealsEndpoint,
                variants,
                store,
                apiParams,
            };
            yield Utils.handleImmediatePaginationCall(batchesMap, apiUtils, paginationObj.current, isDesktop, updateBatchStateCallback, updatePageDataCallback);
        }
        paginationObj.current = Object.assign(Object.assign({}, paginationObj.current), { batchesMap });
    });
    useEffect(getPersonalizedWidgetListOnInitiation, []);
    useEffect(() => {
        const generatePaginationUtils = () => __awaiter(void 0, void 0, void 0, function* () {
            yield paginationUtilsGenerator();
        });
        generatePaginationUtils();
    }, []);
    useEffect(updateDataOnWidgetListChange, [widgetList]);
    const syncImpressionHandler = (impressions, impressionType = IMPRESSION_TYPE.BANNER) => {
        syncImpressionCallback(impressions, impressionType);
        if (isPaginationEnabled) {
            const apiUtils = {
                ApiHelper,
                dealsEndpoint,
                variants,
                store,
                apiParams,
            };
            Utils.handlePaginationBasedOnImpression(impressions, apiUtils, updatePageDataCallback, paginationObj.current, updateBatchStateCallback, !!isDesktop);
        }
    };
    const syncImpressionCallbackInterceptor = syncImpressionCallback ? syncImpressionHandler : null;
    const asyncImpressionCallbackInterceptor = asyncImpressionCallback;
    if (!data.widgetList) {
        return null;
    }
    return (React.createElement(WidgetRenderer, { widgetList: data.widgetList, isServer: isServer, isPersonalizationSupported: isPersonalizationSupported, impressionEvent: impressionEvent, asyncImpressionCallback: asyncImpressionCallbackInterceptor, syncImpressionCallback: syncImpressionCallbackInterceptor, eagerLoadImages: eagerLoadImages, eagerLoadInventories: eagerLoadInventories, theme: theme, isDesktop: !!isDesktop, shouldLoadFonts: shouldLoadFonts, fontCDNPath: fontCDNPath, cta: cta, rootMargin: rootMargin, remoteConfigs: remoteConfigs, store: store }));
};
export default PersonalizedWidgetRenderer;
